import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { rfidService } from '../../services/rfidService';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/common/ConfirmDialog';

interface TransferMatchingConfig {
  id: number;
  name: string;
  description: string;
  status_update_rule: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const TransferMatchingConfigListPage: React.FC = () => {
  const [configs, setConfigs] = useState<TransferMatchingConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await rfidService.getTransferMatchingConfigs();
      // Ensure we're setting a valid array to configs
      setConfigs(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching transfer matching configs:', error);
      enqueueSnackbar('Failed to load transfer matching configurations', { variant: 'error' });
      // Set configs to empty array in case of error
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleDeleteClick = (id: number) => {
    setSelectedConfigId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedConfigId) {
      try {
        await rfidService.deleteTransferMatchingConfig(String(selectedConfigId));
        enqueueSnackbar('Configuration deleted successfully', { variant: 'success' });
        fetchConfigs();
      } catch (error) {
        console.error('Error deleting configuration:', error);
        enqueueSnackbar('Failed to delete configuration', { variant: 'error' });
      }
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedConfigId(null);
  };

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Grid container justifyContent="space-between" alignItems="center" mb={3}>
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Vehicle Transfer Matching Configurations
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={Link}
              to="/rfid/transfer-matching-configs/create"
            >
              Create New Configuration
            </Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status Update Rule</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : !configs || configs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No configurations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    configs.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>{config.name}</TableCell>
                        <TableCell>{config.description}</TableCell>
                        <TableCell>{config.status_update_rule}</TableCell>
                        <TableCell>{config.is_active ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          {new Date(config.created_at).toLocaleDateString('en-US')}
                        </TableCell>
                        <TableCell>
                          {new Date(config.updated_at).toLocaleDateString('en-US')}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/rfid/transfer-matching-configs/${config.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(config.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Configuration"
        content="Are you sure you want to delete this configuration? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Container>
  );
};

export default TransferMatchingConfigListPage;